

// * ========================================================================
// * Make new chart
// * ========================================================================

//import objectAssignDeep from 'object-assign-deep';
import objectAssignDeep from '../../vendor/object-assign';
import responsiveCharts from '../responsive-chart';
import multiselectChart from './../multiselect-chart';
import customTooltip from '../chart-tooltip';
import {
    Chart,
    LineElement,
    BarElement,
    BarController,
    LineController,
    BubbleController,
    LinearScale,
    CategoryScale,
    TimeScale,
    TimeSeriesScale,
    PointElement,
    Legend,
    Title,
    Tooltip,
    Filler
} from 'chart.js';

import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';

class LineWithLine extends LineController {
    draw(ease) {
        super.draw(arguments);
        //Chart.controllers.line.prototype.draw.call(this, ease);

        if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
            // If this is the first visible dataset of this type
            let firstVisibleDataset = null;
            let idx = 0;
            for (let dataset of this.chart.data.datasets) {
                if (dataset.type === 'LineWithLine') {
                    const meta = this.chart.getDatasetMeta(idx);
                    if (!meta.hidden) {
                        firstVisibleDataset = idx;
                        break;
                    }
                }
                idx++;
            }

            // Draw only to first visible
            if (firstVisibleDataset === null || this.index === firstVisibleDataset) {
                let activePoint = this.chart.tooltip._active[0];
                let ctx = this.chart.ctx;
                let x = activePoint.element.x;
                let topY = 8; // this.chart.legend.bottom,
                let bottomY = this.chart.chartArea.bottom;

                // draw line
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, topY);
                ctx.lineTo(x, bottomY);
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#333';
                ctx.stroke();
                ctx.restore();
            }
        }
    }
}

LineWithLine.id = 'LineWithLine';
LineWithLine.defaults = LineController.defaults;

Chart.register(
    LineElement,
    BarElement,
    BarController,
    LineController,
    BubbleController,
    LinearScale,
    CategoryScale,
    TimeScale,
    TimeSeriesScale,
    PointElement,
    Legend,
    Title,
    Tooltip,
    Filler,
    annotationPlugin,
    LineWithLine
);

const textPlugin = {
    id: 'koronatilastot',
    afterDraw(chart, args, options) {
        const {chartArea: {left, top, right, bottom}, ctx} = chart;

        const width = chart.canvas.getBoundingClientRect().width;
        const position = (width / 2) + 40;
        ctx.textAlign = 'center'
        ctx.font = 'normal 14px Courier New';
        ctx.fillStyle = '#bbbbbb';
        ctx.fillText('koronatilastot.fi', position, 30);
    }
};
class CovidChart {

    defaultChartOptions = {
        type: 'line',
        data: {},
        options: {
            locale: 'fi-FI',
            maintainAspectRatio: false,
            responsive: true,
            interaction: {
                mode: 'index', // All values at current index
                intersect: false, // Mouse does not need to be exactly on point
            },
            plugins: {
                title: {
                    display: false,
                },
                legend: {
                    display: false,
                    position: 'bottom'
                },
                koronatilastot: {
                    display: true
                },
                tooltip: {
                    enabled: false,
                    callbacks: {
                        label: function(tooltipItem) {
                            let label = tooltipItem.dataset.label + ': ' || '';
                            label = label + tooltipItem.formattedValue;

                            // Newest value is last
                            var prevIndex = tooltipItem.dataIndex - 1;
                            if (typeof tooltipItem.dataset.data[prevIndex] !== 'undefined') {
                                var prevValue = tooltipItem.dataset.data[prevIndex].y;
                                let change = tooltipItem.raw.y - prevValue;
                                if (change % 1 !== 0) {
                                    change = Math.round(change * 10) / 10;
                                }

                                change = change >= 0 ? '+'+change : change;

                                const hideChange = typeof tooltipItem.dataset.hideChange !== 'undefined' && tooltipItem.dataset.hideChange;
                                if (!hideChange && !isNaN(change)) {
                                    label += ' ('+change+')';
                                }
                            }

                            return label;
                        },
                        labelColor: function(tooltipItem) {
                            const item = tooltipItem.dataset;
                            const color = typeof item.labelColor !== 'undefined' ? item.labelColor : item.type === 'line' || typeof item.backgroundColor === 'undefined' ? item.borderColor : item.backgroundColor;
                            return {
                                backgroundColor: color,
                            };
                        },
                    },
                    filter: function(tooltipItem) {
                        // Filter hidden guide items
                        const item = tooltipItem.dataset;
                        const hideTooltip = (typeof item.id !== 'undefined' && item.id === 'hidden-guide') || (typeof item.tooltipHide !== 'undefined' && item.tooltipHide);
                        const hideZero = typeof item.tooltipHideForZero !== 'undefined' && item.tooltipHideForZero && tooltipItem.formattedValue === '0';
                        return hideTooltip || hideZero ? false : true;
                    },
                    itemSort: function(a, b, data) {
                        return b.datasetIndex > a.datasetIndex ? -1 : 1;
                    },
                },
            },
            scales: {
                x: {
                    type: 'time',
                    tooltipFormat: 'MM/YYYY',
                    display: true,
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'D.M.YY',
                        }
                    }
                },
                y: {
                    display: true,
                    min: 0,
                    ticks: {
                        precision: 0,
                    }
                }
            }
        },
        plugins: [textPlugin]
    };

    defaultStackedBarScales = {
        x: {
            type: 'time',
            stacked: true,
            display: true,
            offset: true,
            time: {
                unit: 'day',
                displayFormats: {
                    day: 'D.M.YY',
                }
            }
        },
        y: {
            display: false,
            stacked: false,
            ticks: {
                precision: 0
            }
        },
        'bar-stacked': {
            axis: 'y',
            stacked: true,
            ticks: {
                precision: 0
            }
        }
    };

    constructor(chartId, settings) {

        this.chartId = chartId;

        this.chartOptions = objectAssignDeep(this.defaultChartOptions, settings.chartOptions);

        // Chart data
        this.chartOptions.data.datasets = settings.datasets;
        if (settings.labels) {
            this.chartOptions.data.labels = settings.labels;
        }

        // Stacked bar chart options (if not defined, arrays cannot be merged)
        if (settings.stackedBar && (!settings.chartOptions || !settings.chartOptions.options || !settings.chartOptions.options.scales)) {
            this.chartOptions.options.scales = this.defaultStackedBarScales;
        }

        // Tooltips
        if (settings.tooltips && settings.tooltips.elementId) {
            const highlightFirstIndex = settings.tooltips.highlightFirstIndex || false;
            const hideHighlight = settings.tooltips.hideHighlight || false;
            const highlightSecondIndex = settings.tooltips.highlightSecondIndex || false;
            const customHeading = settings.tooltips.customHeadingType || false;

            this.chartOptions.options.plugins.tooltip.external = function(data) {
                customTooltip.create(data, settings.tooltips.elementId, {
                    highlightFirstIndex,
                    hideHighlight,
                    highlightSecondIndex,
                    customHeading
                });
            }
        }

        // Multiselect legend click callback (when chart labels are visible and multiselect)
        if (this.chartOptions.options.plugins.legend.display && settings.multiselect) {
            this.chartOptions.options.plugins.legend.onClick = function(e, legend) {
                multiselectChart.legendClick(legend, this.chart, settings.multiselect);
            };
        }

        // Create chart
        const chartCtx = document.getElementById(chartId).getContext('2d');
        this.chart = new Chart(chartCtx, this.chartOptions);

        // Create multiselect for datasets
        if (settings.multiselect) {
            multiselectChart.create(this.chart, this.chart.data.datasets, settings.multiselect);
        }

        // Responsive chart
        //if (settings.responsive) {
        // Update chart options on resize (mobile / desktop)
        responsiveCharts.update(this.chart, settings.responsive);
        const chart = this.chart;

        window.addEventListener('resize', function() {
            responsiveCharts.update(chart, settings.responsive);
        });
        //}

    }

    updateDatasets(datasets, reload = true) {
        this.chart.data.datasets.forEach((dataset, i) => {
            for (let set of datasets) {
                if (set.id === dataset.id) {
                    dataset.data = set.data;
                    dataset.count = set.count;
                    dataset.label = set.label;

                    if (set.segment) {
                        dataset.segment = set.segment;
                    }

                    break;
                }
            }
        });

        if (reload) {
            // Update chart
            this.chart.update();
        }
    }

    updateMultiselect(datasets, options) {
        multiselectChart.updateMultiselect(datasets, options);
    }
}

export default CovidChart;
